import { Spin, Table } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'

import { PaginationMeta } from '../../../../models/pagination'
import { V3Transaction } from '../../../../models/v3-transaction'

import { V3TransactionsColumns } from './TransactionsColumns'
import { isRetrievingData } from '../../../../helpers/empty'
import { styled } from '../../../../theme'
import BulkSettleBar from './BulkSettleBar'

const StyledTable = styled.div<{ data: V3Transaction[] }>`
  margin-top: 25px;

  .ant-spin-container {
    display: flex;
    flex-direction: column;
    min-height: ${(props) => (props.data.length !== 0 ? '80vh' : '0')};
    justify-content: space-between;
  }

  .ant-table-thead > tr > th {
    text-align: left;
  }
  .ant-table-tbody > tr > td {
    text-align: left;
  }
`

interface props {
  loading: boolean
  totalCountLoading: boolean
  isBulkSettleVisible: boolean
  selectedRowKeys: number[]
  bulkSettleLoading: boolean
  transactions: V3Transaction[]
  pagination: PaginationMeta
  showPanel: boolean
  selectedTransaction: V3Transaction | undefined
  onShowPanel: (showPanel: boolean) => void
  onSelectedTransaction: (transaction: V3Transaction, rowIndex?: number) => void
  onPageChange: (page: number) => void
  onPosition: (position: number) => void
  onChargingDurationClick?: (transaction: V3Transaction) => void
  onRowSelect: (record: V3Transaction, selected: boolean) => void
  onSelectAllOnPage: (selected: boolean) => void
  onBulkSettle: () => void
}

const TransactionsTable: React.FC<props> = ({
  loading,
  totalCountLoading,
  isBulkSettleVisible,
  selectedRowKeys,
  bulkSettleLoading,
  transactions,
  pagination,
  showPanel,
  selectedTransaction,
  onSelectedTransaction,
  onShowPanel,
  onPageChange,
  onPosition,
  onChargingDurationClick,
  onRowSelect,
  onSelectAllOnPage,
  onBulkSettle,
}) => {
  const handleClick = (event: React.MouseEvent<any, MouseEvent>, record: V3Transaction, rowIndex?: number) => {
    onSelectedTransaction(record, rowIndex)
    onPosition(event.currentTarget.clientHeight)

    if (showPanel && selectedTransaction && record.id !== selectedTransaction.id) {
      return
    } else {
      return onShowPanel(!showPanel)
    }
  }

  const locale = { emptyText: isRetrievingData(loading) }

  const handleOnRow = (record: V3Transaction, rowIndex?: number) => {
    return {
      onClick: (event: React.MouseEvent<any, MouseEvent>) => handleClick(event, record, rowIndex),
      className: selectedTransaction && showPanel && record.id === selectedTransaction.id ? 'selected-table-row' : '',
    }
  }

  const rowSelection = isBulkSettleVisible
    ? {
        selectedRowKeys,
        onSelect: onRowSelect,
        hideSelectAll: true,
      }
    : undefined

  return (
    <StyledTable data={transactions} className="v3-transactions-table">
      {isBulkSettleVisible && (
        <BulkSettleBar
          transactions={transactions}
          selectedRowKeys={selectedRowKeys}
          bulkSettleLoading={bulkSettleLoading}
          onSelectAllOnPage={onSelectAllOnPage}
          onBulkSettle={onBulkSettle}
        />
      )}
      <Table
        loading={loading}
        rowKey="id"
        rowSelection={rowSelection}
        columns={V3TransactionsColumns(showPanel, onChargingDurationClick)}
        rowClassName={() => 'custom-transaction-row'}
        dataSource={transactions}
        locale={locale}
        onRow={handleOnRow}
        pagination={
          totalCountLoading
            ? false
            : {
                hideOnSinglePage: false,
                position: ['bottomCenter'],
                total: pagination?.totalEntries,
                current: pagination?.currentPage,
                pageSize: pagination?.perPage,
                showSizeChanger: false,
                onChange: onPageChange,
              }
        }
      />
      {totalCountLoading && (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        </div>
      )}
    </StyledTable>
  )
}

export default TransactionsTable

